import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wrench, ArrowRight, AlertCircle } from "lucide-react";

const LOGIN_BG =
    "https://static.prod-images.emergentagent.com/jobs/37fe31ad-6302-4996-acdb-fad565e68d27/images/dfd2547a234bba525e7305b52b8f53f44fd4289bdbbcb9497178342caa2af10d.png";

export default function LoginPage() {
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (user && (user.role === "admin" || user.role === "operador")) {
            navigate("/dashboard", { replace: true });
        } else if (user && user.role === "cliente") {
            navigate("/minha-conta", { replace: true });
        }
    }, [user, navigate]);

    async function onSubmit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            // navigation handled by useEffect based on role
        }
        else setError(res.error || "Erro ao iniciar sessão");
    }

    return (
        <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
            {/* Left visual panel */}
            <div className="relative hidden overflow-hidden bg-zinc-900 lg:block">
                <img
                    src={LOGIN_BG}
                    alt="Mecânica abstrata"
                    className="absolute inset-0 h-full w-full object-cover opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/30 via-zinc-900/10 to-transparent" />
                <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#FF3B30]">
                            <Wrench className="h-5 w-5" />
                        </div>
                        <div className="font-['Outfit'] text-lg font-semibold">Oficina Moto</div>
                    </div>

                    <div>
                        <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">
                            Gestão profissional
                        </div>
                        <h2 className="font-['Outfit'] text-3xl font-semibold leading-tight text-white">
                            Controlo total das entradas
                            <br />
                            da sua oficina.
                        </h2>
                        <p className="mt-4 max-w-md text-sm text-white/75">
                            Registe clientes, motos e marcações. Acompanhe o estado de cada serviço
                            do primeiro contacto até à entrega.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="flex items-center justify-center bg-white px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="mb-10 flex items-center gap-3 lg:hidden">
                        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                            <Wrench className="h-5 w-5" />
                        </div>
                        <div className="font-['Outfit'] text-lg font-semibold">Oficina Moto</div>
                    </div>

                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-500">
                        Acesso de Operador
                    </div>
                    <h1 className="mb-2 font-['Outfit'] text-3xl font-semibold tracking-tight text-zinc-900 md:text-4xl">
                        Bem-vindo de volta.
                    </h1>
                    <p className="mb-8 text-sm text-zinc-600">
                        Inicie sessão para gerir entradas e marcações.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@oficina.pt"
                                required
                                autoComplete="email"
                                data-testid="login-email-input"
                                className="mt-1.5 h-11 rounded-sm border-zinc-300 focus-visible:ring-[#FF3B30]"
                            />
                        </div>

                        <div>
                            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-700">
                                Palavra-passe
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                data-testid="login-password-input"
                                className="mt-1.5 h-11 rounded-sm border-zinc-300 focus-visible:ring-[#FF3B30]"
                            />
                        </div>

                        {error ? (
                            <div
                                data-testid="login-error"
                                className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                            >
                                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        ) : null}

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="h-11 w-full rounded-sm bg-[#FF3B30] text-sm font-semibold tracking-wide hover:bg-[#D63026]"
                        >
                            {loading ? "A entrar..." : (
                                <span className="inline-flex items-center gap-2">
                                    Iniciar sessão
                                    <ArrowRight className="h-4 w-4" />
                                </span>
                            )}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
